import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Home,
  CheckCircle,
  Circle,
  Calendar,
  Key,
  Zap,
  MapPin,
  ClipboardList,
  Loader2,
  RefreshCw,
  AlertTriangle,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MoveOutChecklist() {
  const { checklistId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState(null);
  const [submitting, setSubmitting] = useState({});
  
  // Meter readings state
  const [meterReadings, setMeterReadings] = useState({
    electricity: '',
    gas: '',
    water: ''
  });
  
  // Forwarding address state
  const [forwardingAddress, setForwardingAddress] = useState('');
  
  // Fetch checklist
  const fetchChecklist = useCallback(async () => {
    if (!checklistId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/move-out/${checklistId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setChecklist(data);
        
        // Pre-fill meter readings if already submitted
        if (data.meter_readings) {
          setMeterReadings({
            electricity: data.meter_readings.electricity || '',
            gas: data.meter_readings.gas || '',
            water: data.meter_readings.water || ''
          });
        }
        if (data.forwarding_address) {
          setForwardingAddress(data.forwarding_address);
        }
      } else {
        toast.error('Failed to load checklist');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  }, [checklistId, token]);
  
  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);
  
  // Complete a checklist item
  const handleCompleteItem = async (taskName) => {
    setSubmitting(prev => ({ ...prev, [taskName]: true }));
    
    try {
      const response = await fetch(
        `${API_URL}/api/tenancy-lifecycle/move-out/complete-item?checklist_id=${checklistId}&task_name=${encodeURIComponent(taskName)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Item completed!');
        fetchChecklist();
      } else {
        toast.error('Failed to complete item');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setSubmitting(prev => ({ ...prev, [taskName]: false }));
    }
  };
  
  // Submit meter readings
  const handleSubmitMeterReadings = async () => {
    setSubmitting(prev => ({ ...prev, meters: true }));
    
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/move-out/meter-readings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          checklist_id: checklistId,
          electricity: meterReadings.electricity ? parseFloat(meterReadings.electricity) : null,
          gas: meterReadings.gas ? parseFloat(meterReadings.gas) : null,
          water: meterReadings.water ? parseFloat(meterReadings.water) : null
        })
      });
      
      if (response.ok) {
        toast.success('Meter readings submitted!');
        fetchChecklist();
      } else {
        toast.error('Failed to submit meter readings');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setSubmitting(prev => ({ ...prev, meters: false }));
    }
  };
  
  // Submit forwarding address
  const handleSubmitForwardingAddress = async () => {
    if (!forwardingAddress.trim()) {
      toast.error('Please enter your forwarding address');
      return;
    }
    
    setSubmitting(prev => ({ ...prev, address: true }));
    
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/move-out/forwarding-address`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          checklist_id: checklistId,
          address: forwardingAddress.trim()
        })
      });
      
      if (response.ok) {
        toast.success('Forwarding address saved!');
        fetchChecklist();
      } else {
        toast.error('Failed to save address');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setSubmitting(prev => ({ ...prev, address: false }));
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }
  
  if (!checklist) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Checklist Not Found</h2>
            <p className="text-gray-600 mb-4">
              This move-out checklist could not be found.
            </p>
            <Button onClick={() => navigate('/tenant/dashboard')}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="move-out-checklist-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Move-Out Checklist</h1>
            <p className="text-gray-600">{checklist.property_address}</p>
          </div>
          <Button variant="outline" onClick={fetchChecklist}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Summary Card */}
        <Card>
          <CardContent className="py-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <Calendar className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Notice Date</p>
                <p className="font-semibold">{checklist.notice_date || 'Not set'}</p>
              </div>
              <div className="text-center">
                <Home className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Move-Out Date</p>
                <p className="font-semibold">{checklist.move_out_date || 'Not set'}</p>
              </div>
              <div className="text-center">
                <Key className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Keys Returned</p>
                <p className="font-semibold">{checklist.keys_returned ? 'Yes' : 'Not yet'}</p>
              </div>
              <div className="text-center">
                <ClipboardList className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Progress</p>
                <p className="font-semibold">{checklist.progress?.percent || 0}%</p>
              </div>
            </div>
            
            <div className="mt-6">
              <Progress value={checklist.progress?.percent || 0} className="h-3" />
              <p className="text-sm text-gray-500 mt-2 text-center">
                {checklist.progress?.completed || 0} of {checklist.progress?.total || 0} items completed
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Checklist Items */}
        <Card>
          <CardHeader>
            <CardTitle>Checklist Items</CardTitle>
            <CardDescription>Complete all items before your move-out date</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {checklist.items?.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    item.completed ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                  }`}
                  data-testid={`checklist-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    {item.completed ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <Circle className="w-5 h-5 text-gray-300 mt-0.5" />
                    )}
                    <div>
                      <p className={`font-medium ${item.completed ? 'text-green-800' : 'text-gray-900'}`}>
                        {item.task}
                      </p>
                      <p className="text-sm text-gray-500">{item.description}</p>
                      {item.completed_date && (
                        <p className="text-xs text-green-600 mt-1">
                          Completed: {new Date(item.completed_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {!item.completed && (
                    <Button
                      size="sm"
                      onClick={() => handleCompleteItem(item.task)}
                      disabled={submitting[item.task]}
                      data-testid={`complete-btn-${index}`}
                    >
                      {submitting[item.task] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Mark Done'
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Meter Readings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Final Meter Readings
            </CardTitle>
            <CardDescription>
              Submit your final meter readings to close your utility accounts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="electricity">Electricity (kWh)</Label>
                <Input
                  id="electricity"
                  type="number"
                  value={meterReadings.electricity}
                  onChange={(e) => setMeterReadings(prev => ({ ...prev, electricity: e.target.value }))}
                  placeholder="e.g. 12345"
                  disabled={checklist.meter_readings}
                  data-testid="electricity-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gas">Gas (m³)</Label>
                <Input
                  id="gas"
                  type="number"
                  value={meterReadings.gas}
                  onChange={(e) => setMeterReadings(prev => ({ ...prev, gas: e.target.value }))}
                  placeholder="e.g. 6789"
                  disabled={checklist.meter_readings}
                  data-testid="gas-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="water">Water (m³)</Label>
                <Input
                  id="water"
                  type="number"
                  value={meterReadings.water}
                  onChange={(e) => setMeterReadings(prev => ({ ...prev, water: e.target.value }))}
                  placeholder="e.g. 234"
                  disabled={checklist.meter_readings}
                  data-testid="water-input"
                />
              </div>
            </div>
            
            {checklist.meter_readings ? (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="w-4 h-4 mr-1" />
                Meter readings submitted
              </Badge>
            ) : (
              <Button
                onClick={handleSubmitMeterReadings}
                disabled={submitting.meters}
                data-testid="submit-meters-btn"
              >
                {submitting.meters ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Meter Readings'
                )}
              </Button>
            )}
          </CardContent>
        </Card>
        
        {/* Forwarding Address */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Forwarding Address
            </CardTitle>
            <CardDescription>
              Provide your new address for deposit return and mail forwarding
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forwarding">New Address</Label>
              <textarea
                id="forwarding"
                value={forwardingAddress}
                onChange={(e) => setForwardingAddress(e.target.value)}
                placeholder="Enter your full new address including postcode"
                className="w-full border rounded-lg p-3 h-24"
                disabled={checklist.forwarding_address}
                data-testid="forwarding-address-input"
              />
            </div>
            
            {checklist.forwarding_address ? (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle className="w-4 h-4 mr-1" />
                Forwarding address saved
              </Badge>
            ) : (
              <Button
                onClick={handleSubmitForwardingAddress}
                disabled={submitting.address || !forwardingAddress.trim()}
                data-testid="submit-address-btn"
              >
                {submitting.address ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Forwarding Address'
                )}
              </Button>
            )}
          </CardContent>
        </Card>
        
        {/* Next Steps */}
        {checklist.progress?.percent >= 80 && !checklist.keys_returned && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="py-6">
              <div className="flex items-center gap-4">
                <Key className="w-10 h-10 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-blue-800">Almost Done!</h3>
                  <p className="text-blue-700">
                    Return your keys to complete the move-out process. Contact your landlord or agent to arrange key return.
                  </p>
                </div>
                <ArrowRight className="w-6 h-6 text-blue-600 ml-auto" />
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Completed */}
        {checklist.status === 'completed' && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="py-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-green-800 text-lg">Move-Out Complete!</h3>
              <p className="text-green-700">
                Your move-out process is complete. Your deposit review will begin shortly.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
